import { ApiError, normalizeApiError } from "@/shared/api/client";
import {
  completeGoogleCalendarOAuthRedirect,
  type CalendarStatus,
  type GoogleCalendarAuthorizationIntent,
  type GoogleCalendarOAuthCompletion,
} from "@/shared/api/calendar";
import {
  captureAuthSessionOwner,
  isAuthSessionOwnerCurrent,
  type AuthSessionOwner,
} from "@/shared/auth/session";

const COMPLETION_STORAGE_KEY = "tb_google_calendar_oauth_completion_v1";
const ATTEMPT_FRAGMENT_KEY = "google-calendar-attempt";
const ERROR_FRAGMENT_KEY = "google-calendar-error";
const MAX_OPAQUE_VALUE_LENGTH = 1024;
const STABLE_ERROR_RE = /^[a-z0-9_]{1,100}$/;
const COMPLETION_REQUEST_TIMEOUT_MS = 15_000;
const MAX_RETRY_AFTER_MS = 30_000;

export const GOOGLE_CALENDAR_OAUTH_COMPLETE_PATH = "/oauth/google/calendar/complete";
export const GOOGLE_CALENDAR_OAUTH_HANDOFF_QUERY_KEY = ["calendar-oauth-handoff"] as const;

export type GoogleCalendarOAuthReturn =
  | { kind: "attempt"; attemptId: string }
  | { kind: "error"; code: string }
  | { kind: "missing" };

export type GoogleCalendarOAuthHandoff =
  | {
      owner: AuthSessionOwner;
      outcome: "success";
      connection: CalendarStatus;
      intent: GoogleCalendarAuthorizationIntent;
    }
  | {
      owner: AuthSessionOwner;
      outcome: "error";
      code: string;
    };

export type GoogleCalendarOAuthFlash = {
  source: "google-calendar-oauth";
  outcome: "return";
};

type CaptureDeps = {
  hash?: string;
  pathname?: string;
  search?: string;
  historyState?: unknown;
  replaceUrl?: (state: unknown, url: string) => void;
  storage?: Pick<Storage, "getItem" | "removeItem" | "setItem"> | null;
};

function browserSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function cleanOpaqueValue(value: unknown): string {
  if (typeof value !== "string") return "";
  const normalized = value.trim();
  if (
    !normalized ||
    normalized.length > MAX_OPAQUE_VALUE_LENGTH ||
    /[\u0000-\u001f\u007f]/.test(normalized)
  ) {
    return "";
  }
  return normalized;
}

function cleanStableErrorCode(value: unknown): string {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  return STABLE_ERROR_RE.test(normalized) ? normalized : "google_oauth_callback_invalid";
}

function readStoredAttempt(storage: CaptureDeps["storage"]): string {
  if (!storage) return "";
  try {
    return cleanOpaqueValue(storage.getItem(COMPLETION_STORAGE_KEY));
  } catch {
    return "";
  }
}

function storeAttempt(storage: CaptureDeps["storage"], attemptId: string): void {
  if (!storage) return;
  try {
    storage.setItem(COMPLETION_STORAGE_KEY, attemptId);
  } catch {
    // The current document can still finish even when storage is unavailable.
  }
}

export function clearStoredGoogleCalendarOAuthAttempt(
  storage: CaptureDeps["storage"] = typeof window === "undefined" ? null : browserSessionStorage(),
): void {
  if (!storage) return;
  try {
    storage.removeItem(COMPLETION_STORAGE_KEY);
  } catch {
    // Cleanup is best effort; the server also expires and owner-fences attempts.
  }
}

export function captureGoogleCalendarOAuthReturn(
  deps: CaptureDeps = {},
): GoogleCalendarOAuthReturn {
  if (typeof window === "undefined" && deps.hash === undefined) return { kind: "missing" };
  const hash = deps.hash ?? window.location.hash;
  const pathname = deps.pathname ?? window.location.pathname;
  const search = deps.search ?? window.location.search;
  const historyState = deps.historyState ?? window.history.state;
  const storage = deps.storage === undefined ? browserSessionStorage() : deps.storage;
  const replaceUrl =
    deps.replaceUrl ??
    ((state: unknown, url: string) => {
      window.history.replaceState(state, "", url);
    });

  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const attemptId = cleanOpaqueValue(params.get(ATTEMPT_FRAGMENT_KEY));
  const callbackError = params.has(ERROR_FRAGMENT_KEY)
    ? cleanStableErrorCode(params.get(ERROR_FRAGMENT_KEY))
    : "";

  if (hash) replaceUrl(historyState, `${pathname}${search}`);

  if (callbackError) {
    clearStoredGoogleCalendarOAuthAttempt(storage);
    return { kind: "error", code: callbackError };
  }
  if (attemptId) {
    storeAttempt(storage, attemptId);
    return { kind: "attempt", attemptId };
  }
  const resumedAttemptId = readStoredAttempt(storage);
  return resumedAttemptId ? { kind: "attempt", attemptId: resumedAttemptId } : { kind: "missing" };
}

let initialOAuthReturn: GoogleCalendarOAuthReturn | null = null;

export function captureInitialGoogleCalendarOAuthReturn(): GoogleCalendarOAuthReturn {
  if (initialOAuthReturn) return initialOAuthReturn;
  if (
    typeof window === "undefined" ||
    window.location.pathname !== GOOGLE_CALENDAR_OAUTH_COMPLETE_PATH
  ) {
    return { kind: "missing" };
  }
  initialOAuthReturn = captureGoogleCalendarOAuthReturn();
  return initialOAuthReturn;
}

function sameOwner(left: AuthSessionOwner, right: AuthSessionOwner): boolean {
  return left.userId === right.userId && left.sessionId === right.sessionId;
}

function authorizationSupersededError(): ApiError {
  return new ApiError({
    status: 409,
    code: "calendar_authorization_superseded",
    detail: "The signed-in session changed while Google authorization was completing.",
  });
}

export function googleCalendarOAuthReturnFlash(): GoogleCalendarOAuthFlash {
  return {
    source: "google-calendar-oauth",
    outcome: "return",
  };
}

export function readGoogleCalendarOAuthFlash(value: unknown): GoogleCalendarOAuthFlash | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<GoogleCalendarOAuthFlash>;
  return candidate.source === "google-calendar-oauth" && candidate.outcome === "return"
    ? googleCalendarOAuthReturnFlash()
    : null;
}

export function googleCalendarOAuthSuccessHandoff(
  owner: AuthSessionOwner,
  completion: GoogleCalendarOAuthCompletion,
): GoogleCalendarOAuthHandoff {
  return {
    owner,
    outcome: "success",
    connection: completion.connection,
    intent: completion.authorizationIntent,
  };
}

export function googleCalendarOAuthErrorHandoff(
  owner: AuthSessionOwner,
  code: unknown,
): GoogleCalendarOAuthHandoff {
  return { owner, outcome: "error", code: cleanStableErrorCode(code) };
}

export function readOwnedGoogleCalendarOAuthHandoff(
  value: unknown,
  isOwnerCurrent: (owner: AuthSessionOwner) => boolean = isAuthSessionOwnerCurrent,
): GoogleCalendarOAuthHandoff | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<GoogleCalendarOAuthHandoff>;
  const owner = candidate.owner;
  if (
    !owner ||
    typeof owner.userId !== "string" ||
    typeof owner.sessionId !== "string" ||
    !owner.userId.trim() ||
    !owner.sessionId.trim() ||
    !isOwnerCurrent(owner)
  ) {
    return null;
  }
  if (
    candidate.outcome === "success" &&
    candidate.connection &&
    typeof candidate.connection === "object" &&
    (candidate.intent === "initial-full-access" ||
      candidate.intent === "calendar-write" ||
      candidate.intent === "tasks-write")
  ) {
    return candidate as GoogleCalendarOAuthHandoff;
  }
  if (candidate.outcome === "error") {
    return googleCalendarOAuthErrorHandoff(owner, candidate.code);
  }
  return null;
}

type CompletionRequest = {
  owner: AuthSessionOwner;
  request: Promise<GoogleCalendarOAuthCompletion>;
};

const completionRequests = new Map<string, CompletionRequest>();

export function googleCalendarOAuthCompletionRetryable(error: unknown): boolean {
  const apiError = normalizeApiError(error, "calendar_oauth_complete_failed");
  return (
    apiError.status === 0 ||
    apiError.status === 429 ||
    apiError.status >= 500 ||
    apiError.code === "network_error" ||
    apiError.code === "calendar_oauth_completion_timeout" ||
    apiError.code === "calendar_oauth_completion_in_progress"
  );
}

async function wait(delayMs: number): Promise<void> {
  await new Promise<void>((resolve) => globalThis.setTimeout(resolve, delayMs));
}

function retryAfterMs(error: unknown): number | null {
  const normalized = normalizeApiError(error, "calendar_oauth_complete_failed");
  const raw = normalized.retryAfter || (error as { retryAfter?: unknown } | null)?.retryAfter;
  if (typeof raw !== "string" || !raw.trim()) return null;
  const seconds = Number(raw);
  const delayMs = Number.isFinite(seconds)
    ? seconds * 1_000
    : Date.parse(raw) - Date.now();
  if (!Number.isFinite(delayMs) || delayMs <= 0) return null;
  return Math.min(MAX_RETRY_AFTER_MS, Math.max(250, Math.round(delayMs)));
}

async function completeWithDeadline(
  attemptId: string,
  complete: (
    attemptId: string,
    options?: { signal?: AbortSignal },
  ) => Promise<GoogleCalendarOAuthCompletion>,
  timeoutMs: number,
): Promise<GoogleCalendarOAuthCompletion> {
  const controller = new AbortController();
  const timeoutError = new ApiError({
    status: 0,
    code: "calendar_oauth_completion_timeout",
    detail: "Google Calendar connection confirmation is still pending.",
  });
  let timedOut = false;
  const timer = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort(timeoutError);
  }, Math.max(1, timeoutMs));
  try {
    return await complete(attemptId, { signal: controller.signal });
  } catch (error) {
    if (timedOut) throw timeoutError;
    throw error;
  } finally {
    globalThis.clearTimeout(timer);
  }
}

async function completeWithBoundedRetry(
  attemptId: string,
  complete: (
    attemptId: string,
    options?: { signal?: AbortSignal },
  ) => Promise<GoogleCalendarOAuthCompletion>,
  waitForRetry: (delayMs: number) => Promise<void>,
  requestTimeoutMs: number,
  assertOwnerCurrent: () => void,
): Promise<GoogleCalendarOAuthCompletion> {
  // A prior document or callback retry can still own the idempotent token
  // exchange. Give a legitimately slow provider round-trip time to settle
  // instead of turning a successful consent into an early failure message.
  const delaysMs = [0, 250, 750, 1_500, 3_000, 5_000, 7_500, 10_000] as const;
  let lastError: unknown;
  for (const delayMs of delaysMs) {
    const providerDelayMs = retryAfterMs(lastError);
    const nextDelayMs = providerDelayMs ?? delayMs;
    if (nextDelayMs) await waitForRetry(nextDelayMs);
    assertOwnerCurrent();
    try {
      const result = await completeWithDeadline(attemptId, complete, requestTimeoutMs);
      assertOwnerCurrent();
      return result;
    } catch (error) {
      assertOwnerCurrent();
      lastError = error;
      if (!googleCalendarOAuthCompletionRetryable(error)) throw error;
    }
  }
  throw lastError;
}

export function completeGoogleCalendarOAuthOnce(
  attemptId: string,
  deps: {
    complete?: (
      attemptId: string,
      options?: { signal?: AbortSignal },
    ) => Promise<GoogleCalendarOAuthCompletion>;
    isOwnerCurrent?: (owner: AuthSessionOwner) => boolean;
    owner?: AuthSessionOwner;
    requestTimeoutMs?: number;
    wait?: (delayMs: number) => Promise<void>;
  } = {},
): Promise<GoogleCalendarOAuthCompletion> {
  const normalizedAttemptId = cleanOpaqueValue(attemptId);
  if (!normalizedAttemptId) {
    return Promise.reject(
      Object.assign(new Error("google_oauth_attempt_required"), {
        code: "google_oauth_attempt_required",
        status: 400,
      }),
    );
  }
  const owner = deps.owner || captureAuthSessionOwner();
  const isOwnerCurrent = deps.isOwnerCurrent || isAuthSessionOwnerCurrent;
  if (!owner || !isOwnerCurrent(owner)) return Promise.reject(authorizationSupersededError());
  const existing = completionRequests.get(normalizedAttemptId);
  if (existing) {
    return sameOwner(existing.owner, owner)
      ? existing.request
      : Promise.reject(authorizationSupersededError());
  }
  const entry = {} as CompletionRequest;
  entry.owner = owner;
  entry.request = completeWithBoundedRetry(
    normalizedAttemptId,
    deps.complete || completeGoogleCalendarOAuthRedirect,
    deps.wait || wait,
    deps.requestTimeoutMs || COMPLETION_REQUEST_TIMEOUT_MS,
    () => {
      if (!isOwnerCurrent(owner)) throw authorizationSupersededError();
    },
  ).finally(() => {
    if (completionRequests.get(normalizedAttemptId) === entry) {
      completionRequests.delete(normalizedAttemptId);
    }
  });
  completionRequests.set(normalizedAttemptId, entry);
  return entry.request;
}
